import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtL, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

const inp = "w-full bg-app border border-border rounded px-2 py-1 text-xs text-right";

function Modal({ title, m, children, footer, wide, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className={`bg-card border border-border rounded-lg w-full ${wide ? "max-w-4xl" : "max-w-md"} max-h-[92vh] overflow-y-auto`} data-testid={testid}>
        <div className="p-4 border-b border-border flex justify-between items-center sticky top-0 bg-card">
          <h3 className="font-heading text-lg">{title}</h3>
          <BotonCerrar onClick={m.cerrar} />
        </div>
        <div className="p-4">{children}</div>
        {footer && <div className="p-4 border-t border-border flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

// Horas extra, bonificaciones y otras deducciones por empleado antes de calcular (13.2).
export function ExtrasModal({ period, onClose, onSaved }) {
  const [emps, setEmps] = useState([]);
  const [ex, setEx] = useState(period.extras || {});
  useEffect(() => {
    api.get("/hr/employees", { params: { estado: "activo" } }).then(({ data }) => {
      const ids = period.incluye_empleados || [];
      setEmps(ids.length ? data.filter((e) => ids.includes(e.id)) : data);
    }).catch(() => {});
  }, [period]);
  const upd = (id, k, v) => setEx({ ...ex, [id]: { ...(ex[id] || {}), [k]: v } });
  const save = async () => {
    const clean = {};
    Object.entries(ex).forEach(([id, v]) => {
      clean[id] = { horas_extra_cantidad: Number(v.horas_extra_cantidad || 0), horas_extra_monto: Number(v.horas_extra_monto || 0),
        bonificaciones: Number(v.bonificaciones || 0), otras_deducciones: Number(v.otras_deducciones || 0),
        descripcion_otras_deducciones: v.descripcion_otras_deducciones || "" };
    });
    try { await api.put(`/hr/payroll/periods/${period.id}/extras`, { extras: clean }); toast.success("Novedades guardadas"); onSaved(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const m = useModalForm({ value: ex, onClose, onSave: save });
  return (
    <Modal title={`Novedades de planilla — ${period.nombre}`} m={m} wide testid="extras-modal"
      footer={<><button onClick={m.cerrar} className="px-3 py-1.5 text-sm border border-border rounded">Cancelar</button>
        <button onClick={m.guardar} disabled={m.saving} className="px-4 py-1.5 text-sm bg-agro-green text-black font-medium rounded disabled:opacity-40" data-testid="extras-save">{m.saving ? "Guardando…" : "Guardar"}</button></>}>
      <p className="text-xs text-text-muted mb-3">Se aplican al calcular. GRATIVA (acumulada liberada), viáticos reembolsables y cuotas de préstamos se toman automáticamente.</p>
      <table className="w-full text-xs">
        <thead className="bg-sidebar"><tr className="text-[10px] uppercase text-text-muted text-left">
          <th className="p-2">Empleado</th><th className="p-2 text-right"># Horas extra</th><th className="p-2 text-right">Monto H.E. (L)</th>
          <th className="p-2 text-right">Bonificaciones</th><th className="p-2 text-right">Otras deducc.</th><th className="p-2">Descripción deducción</th>
        </tr></thead>
        <tbody>
          {emps.map((e) => (
            <tr key={e.id} className="border-t border-border/30">
              <td className="p-2">{e.nombre}<div className="text-[10px] text-text-muted">{e.codigo}</div></td>
              <td className="p-1"><input type="number" className={inp} value={ex[e.id]?.horas_extra_cantidad ?? ""} onChange={(v) => upd(e.id, "horas_extra_cantidad", v.target.value)} /></td>
              <td className="p-1"><input type="number" className={inp} value={ex[e.id]?.horas_extra_monto ?? ""} onChange={(v) => upd(e.id, "horas_extra_monto", v.target.value)} data-testid={`ex-he-${e.codigo}`} /></td>
              <td className="p-1"><input type="number" className={inp} value={ex[e.id]?.bonificaciones ?? ""} onChange={(v) => upd(e.id, "bonificaciones", v.target.value)} /></td>
              <td className="p-1"><input type="number" className={inp} value={ex[e.id]?.otras_deducciones ?? ""} onChange={(v) => upd(e.id, "otras_deducciones", v.target.value)} /></td>
              <td className="p-1"><input className="w-full bg-app border border-border rounded px-2 py-1 text-xs" value={ex[e.id]?.descripcion_otras_deducciones ?? ""} onChange={(v) => upd(e.id, "descripcion_otras_deducciones", v.target.value)} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </Modal>
  );
}

// Paso de PAGO de planilla: tesorería + asiento Dr sueldos por pagar / Cr banco.
export function PayModal({ period, total, onClose, onPaid }) {
  const [accounts, setAccounts] = useState([]);
  const [f, setF] = useState({ cuenta_id: "", referencia: "", fecha: new Date().toISOString().slice(0, 10) });
  useEffect(() => { api.get("/treasury/accounts").then(({ data }) => setAccounts(data || [])).catch(() => {}); }, []);
  const pay = async () => {
    try { await api.post(`/hr/payroll/periods/${period.id}/pay`, f); toast.success("Planilla pagada"); onPaid(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const m = useModalForm({ value: f, onClose, onSave: pay, mensaje: "¿Está seguro de que desea registrar el pago de la planilla?" });
  return (
    <Modal title={`Pagar planilla — ${period.nombre}`} m={m} testid="pay-modal"
      footer={<><button onClick={m.cerrar} className="px-3 py-1.5 text-sm border border-border rounded">Cancelar</button>
        <button onClick={m.guardar} disabled={m.saving} className="px-4 py-1.5 text-sm bg-agro-green text-black font-medium rounded disabled:opacity-40" data-testid="pay-confirm">{m.saving ? "Registrando…" : "Registrar pago"}</button></>}>
      <div className="space-y-3 text-sm">
        <div className="bg-app rounded p-3">Neto a pagar: <b className="text-agro-green">{fmtL(total)}</b></div>
        <div><label className="text-[9px] uppercase text-text-muted">Cuenta de tesorería</label>
          <select value={f.cuenta_id} onChange={(e) => setF({ ...f, cuenta_id: e.target.value })} className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm" data-testid="pay-cuenta">
            <option value="">— Banco por defecto —</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.codigo} · {a.nombre}</option>)}
          </select></div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-[9px] uppercase text-text-muted">Fecha de pago</label>
            <input type="date" value={f.fecha} onChange={(e) => setF({ ...f, fecha: e.target.value })} className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm" /></div>
          <div><label className="text-[9px] uppercase text-text-muted">Referencia</label>
            <input value={f.referencia} onChange={(e) => setF({ ...f, referencia: e.target.value })} className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm" /></div>
        </div>
        <p className="text-[11px] text-text-muted">Asiento: Dr Sueldos por pagar / Cr banco. Se registra el egreso en tesorería y la planilla queda en estado <b>pagado</b>.</p>
      </div>
    </Modal>
  );
}

// Reporte de planilla por departamento (endpoint /hr/reports/planilla-departamento).
export function DeptReport({ periodId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/hr/reports/planilla-departamento", { params: { period_id: periodId } }).then(({ data }) => setRows(data.rows)).catch(() => setRows([])); }, [periodId]);
  if (!rows) return null;
  return (
    <div className="p-3 border-t border-border/40" data-testid="dept-report">
      <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Planilla por departamento</div>
      <table className="w-full text-xs">
        <thead><tr className="text-[10px] text-text-muted text-left"><th className="p-1">Departamento</th><th className="p-1 text-right">#</th><th className="p-1 text-right">Devengado</th><th className="p-1 text-right">Cargas</th><th className="p-1 text-right">Neto</th><th className="p-1 text-right">Costo total</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.departamento} className="border-t border-border/30"><td className="p-1">{r.departamento}</td><td className="p-1 text-right">{r.n_empleados}</td><td className="p-1 text-right">{fmtL(r.devengado)}</td><td className="p-1 text-right">{fmtL(r.cargas_patronales)}</td><td className="p-1 text-right">{fmtL(r.neto)}</td><td className="p-1 text-right text-agro-green">{fmtL(r.costo_total)}</td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}
